import { useEffect } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { ButtonInLink } from '@hivebrite/design-system/ButtonInLink';
import { Card } from '@hivebrite/design-system/Card';
import { Container } from '@hivebrite/design-system/Container';
import { Divider } from '@hivebrite/design-system/Divider';
import { useToggle } from '@hivebrite/react-hooks';

import SuccessConfetti from 'hb-react/frontoffice/components/SuccessConfetti';
import { useSessionInfoContext } from 'hb-react/frontoffice/contexts/SessionInfoContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import Sidebar from 'hb-react/shared/apps/Memberships/components/Sidebar';
import Grid, { Col, Row } from 'hb-react/shared/components/DesignSystem/Grid';
import { useShoppingCard } from 'hb-react/shared/components/ShoppingCard/hooks/useShoppingCard';
import UpdateInformationModal from 'hb-react/shared/components/User/UpdateInformationModal';
import { useUserDataSubscriber } from 'hb-react/shared/components/User/UpdateInformationModal/hooks/useUserDataSubscriber';
import SuccessCircleCheck from 'hb-react/shared/icons/SuccessCircleCheck';

const OrderCompleted = ({ headline }) => {
  const { data, isLoading } = useShoppingCard();
  const { open, isOpen, close } = useToggle();
  const { currentUser: user } = useDefaultAppContexts();
  const { memberships: membershipsSessionInfo } = useSessionInfoContext();

  const { confetti_on_success: displayConfetti } =
    membershipsSessionInfo?.programme || {};

  const {
    cart: { billing_address: billingAddress, cart_items: cartItems, payload },
    _metadata: { success_page: successPage } = {},
    associated_items: { memberships = [] } = {},
  } = data;

  const { isModified, changeSet } = useUserDataSubscriber(
    {
      user,
      formState: {
        ...payload,
        billing_address_attributes: {
          ...billingAddress,
          postal_address_type: payload?.postal_address_type,
        },
      },
    },
    membershipsSessionInfo?.user_custom_attributes,
  );

  useEffect(() => {
    if (isModified) {
      open();
    }
  }, [isModified, open]);

  if (isLoading) {
    return null;
  }

  return (
    <Container height="auto">
      {displayConfetti && <SuccessConfetti duration={3000} />}
      <Box display="block" width="100%" pt={8} pb={8}>
        <Grid rowGap={0}>
          <Row>
            <LeftColumn>
              <Box display="block">
                <Card width="100%">
                  {headline && (
                    <>
                      <Box>
                        <Box color="grass">
                          <SuccessCircleCheck
                            title={__('Success')}
                            width="30px"
                            height="30px"
                            data-testid="success-circle-check"
                          />
                        </Box>
                        <Box font="h3-semibold" pl="4" pt="1">
                          {headline}
                        </Box>
                      </Box>
                      <Divider
                        borderBottomColor="#cdcdcd"
                        mt={3}
                        width="100%"
                      />
                    </>
                  )}
                  <Box
                    flexDirection="column"
                    dangerouslySetInnerHTML={{
                      __html: successPage,
                    }}
                    data-testid="success-message-content"
                  />
                </Card>
                <NavBar />
              </Box>
            </LeftColumn>
            <Col
              width={({ numCols }) => ({
                _: 1,
                l: 4 / numCols,
                xl: 3 / numCols,
              })}
              offset={({ numCols }) => ({
                _: 0,
                l: 1 / numCols,
              })}
              order={{ _: 1, l: 2 }}
            >
              <Sidebar
                cartItems={cartItems}
                memberships={memberships}
                topBoundary="header"
                offset="25px"
              />
            </Col>
          </Row>
        </Grid>
      </Box>
      {isOpen && (
        <UpdateInformationModal
          isOpen
          changeSet={changeSet}
          onSuccess={close}
          onClose={close}
        />
      )}
    </Container>
  );
};

const NavBar = () => (
  <Box mt={6}>
    <ButtonInLink
      linkProps={{
        to: Routes.me_my_memberships_path(),
      }}
    >
      {s__('Memberships|See my memberships')}
    </ButtonInLink>
    <ButtonInLink
      linkProps={{
        to: Routes.memberships_path(),
      }}
      ml={4}
      variant="secondary"
    >
      {s__('Memberships|Go to memberships page')}
    </ButtonInLink>
  </Box>
);

export default OrderCompleted;

const LeftColumn = styled(Col).attrs({
  width: ({ numCols }) => ({
    _: 1,
    l: 7 / numCols,
    xl: 6 / numCols,
  }),
  offset: ({ numCols }) => ({
    _: 0,
    xl: 1 / numCols,
  }),
  order: { _: 2, l: 1 },
})``;
